// src/encounter/lib/to-procedure-order.ts
// Map a DS CodeSearchResult (from the procedure picker's CodeSearchField) + the dialog's captured
// fields → a DS PlanOrder (attached to a Dx on the AssessmentPlanCanvas) + the CodedOrderPayload
// for the order side-map (order-payload.ts). PURE — mirrors to-plan-order (medication).
//
// The picker toggles CPT vs SNOMED; `system` picks the wire slot the save mapper will populate
// (procedureCode vs snomedCode). The caller supplies the orderId (unique per attachment — the same
// procedure can legitimately be ordered against more than one Dx, so the id is NOT derived from the
// code, which would collide). addToBilling defaults false; a blank/whitespace note is dropped.
import type { CodeSearchResult, PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload, OrderCoding } from './order-payload';

export interface ProcedureCaptured {
  /** CPT (procedureCode) vs SNOMED (snomedCode) — picks the wire slot in the save mapper. */
  system: 'cpt' | 'snomed';
  addToBilling?: boolean;
  note?: string;
}

export interface ProcedureOrder {
  planOrder: PlanOrder;
  payload: Extract<CodedOrderPayload, { kind: 'procedure' }>;
}

export function toProcedureOrder(
  result: CodeSearchResult,
  captured: ProcedureCaptured,
  orderId: string,
): ProcedureOrder {
  const display = (result.display || result.code || '').trim();
  const code: OrderCoding = {
    code: result.code ?? '',
    displayName: display,
    ...(result.system ? { system: result.system } : {}),
  };
  const note = captured.note?.trim();
  return {
    // dxIds empty — the host attaches the target Dx when it appends the order (v2 PROD-1422).
    planOrder: { id: orderId, type: 'procedure', label: display, dxIds: [] },
    payload: {
      kind: 'procedure',
      system: captured.system,
      code,
      addToBilling: captured.addToBilling ?? false,
      ...(note ? { note } : {}),
    },
  };
}
